export type MagnusChatOutcome =
  | { kind: "answered"; text: string; messageId?: string }
  | { kind: "awaiting-approval"; summary: string; messageId?: string }
  | {
      kind: "failed";
      message: string;
      messageId?: string;
      /**
       * The turn never produced a reply, so submitting it again cannot
       * duplicate one. The runner retries such a failure once.
       */
      retryable?: boolean;
    };

export type MagnusChatAttempts = 1 | 2;

export type MagnusChatDelegate = (
  query: string,
  signal: AbortSignal,
  /** See `callContextNote`; travels as hidden metadata, not message text. */
  callContext?: string
) => Promise<MagnusChatOutcome>;

export type MagnusChatDelegationStatus =
  | "completed"
  | "failed"
  | "timed-out"
  | "interrupted"
  | "awaiting-approval";

export type MagnusChatDelegation = {
  toolCallId: string;
  query: string;
  status: MagnusChatDelegationStatus;
  messageId?: string;
  startedAt: string;
  /** Present on the final report; 2 when the first attempt was retried. */
  attempts?: MagnusChatAttempts;
};

export type MagnusChatToolResult = {
  toolCallId: string;
  output: string;
};

export const MAGNUS_CHAT_TIMEOUT_MS = 110_000;
export const MAGNUS_CHAT_RETRY_DELAY_MS = 1500;
export const SPOKEN_RESULT_MAX_BYTES = 6000;

const STILL_WORKING =
  "The chat is still working on this. The answer will show up in the chat when it's ready.";
const BUSY =
  "The chat is still working on the previous request. Ask the user to wait for that one first.";
const CANCELLED =
  "The user cancelled this request and it was stopped. Don't bring it up again.";
const TRUNCATED_SUFFIX = " … (cut short; the full answer is in the chat)";

const encoder = new TextEncoder();

function byteLength(text: string): number {
  return encoder.encode(text).length;
}

export function toSpeakableText(
  markdown: string,
  maxBytes: number = SPOKEN_RESULT_MAX_BYTES
): string {
  let text = markdown
    .replace(/```[\s\S]*?```/g, " (code omitted) ")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[\^[^\]]*\]/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/https?:\/\/\S+/g, "a link")
    .replace(/^[ \t]*\|?[ \t]*:?-{3,}[ \t|:-]*$/gm, "")
    .replace(/^[ \t]*\|(.*)\|[ \t]*$/gm, (_, row: string) =>
      row
        .split("|")
        .map((cell) => cell.trim())
        .filter(Boolean)
        .join(", ")
    )
    .replace(/^#{1,6}[ \t]+/gm, "")
    .replace(/^[ \t]*>[ \t]?/gm, "")
    .replace(/(\*\*|__)(.+?)\1/g, "$2")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/^[ \t]*[-*+][ \t]+/gm, "- ")
    .replace(/[ \t]+/g, " ")
    .replace(/ ?\n ?/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();

  if (byteLength(text) <= maxBytes) {
    return text;
  }
  const budget = maxBytes - byteLength(TRUNCATED_SUFFIX);
  let cut = text.slice(0, budget);
  while (byteLength(cut) > budget) {
    cut = cut.slice(0, Math.floor(cut.length * 0.95));
  }
  const lastSpace = cut.lastIndexOf(" ");
  text = lastSpace > budget / 2 ? cut.slice(0, lastSpace) : cut;
  return `${text}${TRUNCATED_SUFFIX}`;
}

export const CALL_CONTEXT_MAX_CHARS = 1200;

type SpokenTurn = { role: "user" | "assistant"; text: string; startMs: number };

export function recentCallContext(
  turns: readonly SpokenTurn[],
  delegatedAtMs: readonly number[],
  maxChars: number = CALL_CONTEXT_MAX_CHARS
): string {
  const skip = new Set<number>();
  for (const atMs of delegatedAtMs) {
    const index = turns.findLastIndex(
      (turn) => turn.role === "user" && turn.startMs <= atMs
    );
    if (index < 0) {
      continue;
    }
    skip.add(index);
    if (turns[index + 1]?.role === "assistant") {
      skip.add(index + 1);
    }
  }
  const lines: string[] = [];
  let used = 0;
  for (let i = turns.length - 1; i >= 0; i--) {
    const turn = turns[i];
    const text = turn?.text.trim();
    if (!(turn && text) || skip.has(i)) {
      continue;
    }
    const line = `${turn.role === "user" ? "User" : "Magnus"}: ${text}`;
    if (used + line.length > maxChars) {
      break;
    }
    lines.unshift(line);
    used += line.length + 1;
  }
  return lines.join("\n");
}

export function callContextNote(
  ...notes: readonly unknown[]
): string | undefined {
  const text = notes
    .map((note) =>
      typeof note === "string"
        ? note.trim().slice(0, CALL_CONTEXT_MAX_CHARS)
        : ""
    )
    .filter(Boolean)
    .join("\n");
  return text || undefined;
}

function outcomeToResult(
  toolCallId: string,
  outcome: MagnusChatOutcome
): { result: MagnusChatToolResult; status: MagnusChatDelegationStatus } {
  switch (outcome.kind) {
    case "answered": {
      const spoken = toSpeakableText(outcome.text);
      return {
        status: "completed",
        result: {
          toolCallId,
          output: spoken || "Done. The reply is in the chat.",
        },
      };
    }
    case "awaiting-approval":
      return {
        status: "awaiting-approval",
        result: {
          toolCallId,
          output: `The chat needs the user's approval before it continues: ${toSpeakableText(
            outcome.summary,
            2048
          )}. Ask them to approve it on screen.`,
        },
      };
    default:
      return {
        status: "failed",
        result: {
          toolCallId,
          output: `The chat couldn't finish that: ${outcome.message || "something went wrong"}.`,
        },
      };
  }
}

const MAGNUS_CHAT_RELEASE_GRACE_MS = 3000;

/** Waits for an aborted delegate to settle, but never longer than `ms`. */
function letGo(settled: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    settled.then(
      () => undefined,
      () => undefined
    ),
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

type InFlight = {
  toolCallId: string;
  controller: AbortController;
  done: Promise<void>;
  finish: () => void;
};

export type MagnusChatToolRunner = {
  handle: (call: {
    toolCallId: string;
    parameters: Record<string, unknown>;
  }) => Promise<void>;
  cancel: () => void;
  /**
   * Drops the in-flight request silently; resolves once its delegate has
   * settled (or the release grace ran out).
   */
  interrupt: () => Promise<void>;
  dispose: () => void;
  isBusy: () => boolean;
};

export function createMagnusChatToolRunner(opts: {
  delegate: MagnusChatDelegate;
  sendResult: (result: MagnusChatToolResult) => void;
  onDelegation?: (
    delegation: Omit<MagnusChatDelegation, "status"> & {
      status: MagnusChatDelegationStatus | null;
    }
  ) => void;
  /** Spoken turns the request builds on; see `recentCallContext`. */
  callContext?: () => string;
  timeoutMs?: number;
  retryDelayMs?: number;
  /** How long an aborted delegate gets to settle before it's let go anyway. */
  releaseGraceMs?: number;
}): MagnusChatToolRunner {
  const timeoutMs = opts.timeoutMs ?? MAGNUS_CHAT_TIMEOUT_MS;
  const retryDelayMs = opts.retryDelayMs ?? MAGNUS_CHAT_RETRY_DELAY_MS;
  const releaseGraceMs = opts.releaseGraceMs ?? MAGNUS_CHAT_RELEASE_GRACE_MS;
  let inFlight: InFlight | null = null;
  let disposed = false;

  const attemptOnce = async (
    query: string,
    signal: AbortSignal,
    callContext: string | undefined
  ): Promise<MagnusChatOutcome> => {
    try {
      return await opts.delegate(query, signal, callContext);
    } catch (err) {
      return {
        kind: "failed",
        message: err instanceof Error ? err.message : String(err),
      };
    }
  };

  const pause = (signal: AbortSignal) =>
    retryDelayMs > 0 && !signal.aborted
      ? new Promise<void>((resolve) => {
          let timer: ReturnType<typeof setTimeout> | undefined;
          const done = () => {
            clearTimeout(timer);
            signal.removeEventListener("abort", done);
            resolve();
          };
          timer = setTimeout(done, retryDelayMs);
          signal.addEventListener("abort", done, { once: true });
        })
      : Promise.resolve();

  const delegateWithRetry = async (
    query: string,
    signal: AbortSignal,
    callContext: string | undefined,
    onAttempt: (attempts: MagnusChatAttempts) => void
  ): Promise<MagnusChatOutcome> => {
    const first = await attemptOnce(query, signal, callContext);
    if (
      first.kind !== "failed" ||
      !first.retryable ||
      signal.aborted ||
      disposed
    ) {
      return first;
    }
    await pause(signal);
    if (signal.aborted || disposed) {
      return first;
    }
    onAttempt(2);
    return attemptOnce(query, signal, callContext);
  };

  const handle: MagnusChatToolRunner["handle"] = async ({
    toolCallId,
    parameters,
  }) => {
    const query =
      typeof parameters.query === "string" ? parameters.query.trim() : "";
    if (!query) {
      opts.sendResult({
        toolCallId,
        output: "No request was given. Ask the user what they need.",
      });
      return;
    }
    if (inFlight || disposed) {
      opts.sendResult({
        toolCallId,
        output: BUSY,
      });
      return;
    }

    const controller = new AbortController();
    let finish: () => void = () => undefined;
    const done = new Promise<void>((resolve) => {
      finish = resolve;
    });
    const current: InFlight = { toolCallId, controller, done, finish };
    inFlight = current;
    const release = () => {
      if (inFlight === current) {
        inFlight = null;
      }
      current.finish();
    };
    let timer: ReturnType<typeof setTimeout> | undefined;
    // A throwing callback must not leave the runner busy for the rest of
    // the call.
    try {
      const startedAt = new Date().toISOString();
      let attempts: MagnusChatAttempts = 1;
      opts.onDelegation?.({ toolCallId, query, status: null, startedAt });

      const timedOut = new Promise<"timeout">((resolve) => {
        timer = setTimeout(() => resolve("timeout"), timeoutMs);
      });
      const aborted = new Promise<"aborted">((resolve) => {
        controller.signal.addEventListener("abort", () => resolve("aborted"), {
          once: true,
        });
      });
      const settled = delegateWithRetry(
        query,
        controller.signal,
        callContextNote(parameters.context, opts.callContext?.()),
        (n) => {
          attempts = n;
        }
      );

      const winner = await Promise.race([settled, timedOut, aborted]);
      clearTimeout(timer);

      if (winner === "aborted") {
        if (controller.signal.reason === "cancelled") {
          opts.sendResult({ toolCallId, output: CANCELLED });
        }
        opts.onDelegation?.({
          toolCallId,
          query,
          status: "interrupted",
          startedAt,
          attempts,
        });
        await letGo(settled, releaseGraceMs);
        return;
      }
      release();
      if (winner === "timeout") {
        controller.abort("timeout");
        opts.sendResult({ toolCallId, output: STILL_WORKING });
        opts.onDelegation?.({
          toolCallId,
          query,
          status: "timed-out",
          startedAt,
          attempts,
        });
        return;
      }
      const { result, status } = outcomeToResult(toolCallId, winner);
      opts.sendResult(result);
      opts.onDelegation?.({
        toolCallId,
        query,
        status,
        startedAt,
        attempts,
        ...(winner.messageId ? { messageId: winner.messageId } : {}),
      });
    } finally {
      clearTimeout(timer);
      release();
    }
  };

  return {
    handle,
    cancel: () => inFlight?.controller.abort("cancelled"),
    interrupt: () => {
      const current = inFlight;
      if (!current) {
        return Promise.resolve();
      }
      current.controller.abort("interrupted");
      return current.done;
    },
    dispose: () => {
      disposed = true;
      inFlight?.controller.abort("hangup");
    },
    isBusy: () => inFlight !== null,
  };
}
